import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../common/Modal';
import { getBeacons, addBeacon, removeBeacon } from '../../api/apartments';
import { Beacon } from '../../types';
import { toast } from '../common/Toast';
import axios from 'axios';

interface BeaconManageModalProps {
  isOpen: boolean;
  onClose: () => void;
  apartmentId: string;
  onUpdate: () => void;
}

interface NewBeaconRow {
  beacon_hw_id: string;
  type: 'entry' | 'exit';
  name: string;
}

const emptyRow: NewBeaconRow = { beacon_hw_id: '', type: 'entry', name: '' };

const errorMessages: Record<string, string> = {
  BEACON_IN_USE: '이 비콘 HW ID는 다른 장소에서 사용 중입니다',
  BEACON_DUPLICATE: '동일한 비콘 HW ID가 이미 이 장소에 등록되어 있습니다',
  BEACON_LIMIT_EXCEEDED: '장소당 최대 10개의 비콘만 등록할 수 있습니다',
  MIN_BEACON_REQUIRED: '해당 유형의 마지막 비콘은 삭제할 수 없습니다',
};

const BeaconManageModal: React.FC<BeaconManageModalProps> = ({
  isOpen,
  onClose,
  apartmentId,
  onUpdate,
}) => {
  const [beacons, setBeacons] = useState<Beacon[]>([]);
  const [loading, setLoading] = useState(false);
  const [newRow, setNewRow] = useState<NewBeaconRow | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchBeacons = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBeacons(apartmentId);
      setBeacons(res);
    } catch {
      toast.error('비콘 목록을 불러올 수 없습니다');
    } finally {
      setLoading(false);
    }
  }, [apartmentId]);

  useEffect(() => {
    if (isOpen) {
      fetchBeacons();
      setNewRow(null);
    }
  }, [isOpen, fetchBeacons]);

  const handleErrorCode = (err: unknown) => {
    if (axios.isAxiosError(err)) {
      const code = (err.response?.data as { error_code?: string })?.error_code;
      if (code && errorMessages[code]) {
        toast.error(errorMessages[code]);
        return;
      }
    }
    toast.error('요청에 실패했습니다');
  };

  const handleAdd = async () => {
    if (!newRow) return;
    const hwId = Number(newRow.beacon_hw_id);
    if (!hwId || isNaN(hwId)) {
      toast.error('비콘 HW ID를 입력해 주세요');
      return;
    }
    setSaving(true);
    try {
      await addBeacon(apartmentId, {
        beacon_hw_id: hwId,
        type: newRow.type,
        name: newRow.name.trim() || undefined,
      });
      toast.success('비콘이 추가되었습니다');
      setNewRow(null);
      fetchBeacons();
      onUpdate();
    } catch (err) {
      handleErrorCode(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (beacon: Beacon) => {
    if (!window.confirm(`비콘 HW ${beacon.beacon_hw_id}을(를) 삭제하시겠습니까?`)) return;
    try {
      const result = await removeBeacon(beacon.id);
      toast.success('비콘이 삭제되었습니다');
      if (result.warning) {
        toast.warning(result.warning);
      }
      fetchBeacons();
      onUpdate();
    } catch (err) {
      handleErrorCode(err);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="비콘 관리">
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px 0', color: '#999' }}>
          로딩 중...
        </div>
      ) : (
        <div>
          {beacons.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 13, color: 'var(--color-text-secondary)' }}>HW ID</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 13, color: 'var(--color-text-secondary)' }}>유형</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 13, color: 'var(--color-text-secondary)' }}>이름</th>
                  <th style={{ padding: '6px 8px', width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {beacons.map((b) => (
                  <tr key={b.id} style={{ borderBottom: '1px solid var(--color-border-light)' }}>
                    <td style={{ padding: '8px' }}>{b.beacon_hw_id}</td>
                    <td style={{ padding: '8px' }}>
                      <span className={`badge ${b.type === 'entry' ? 'badge--green' : 'badge--orange'}`}>
                        {b.type === 'entry' ? '입차' : '출차'}
                      </span>
                    </td>
                    <td style={{ padding: '8px', color: b.name ? undefined : '#999' }}>{b.name || '-'}</td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>
                      <button
                        className="btn btn--sm btn--danger"
                        onClick={() => handleDelete(b)}
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {beacons.length === 0 && !newRow && (
            <p style={{ textAlign: 'center', color: '#999', padding: '12px 0' }}>
              등록된 비콘이 없습니다
            </p>
          )}

          {newRow ? (
            <div style={{ background: 'var(--color-bg-secondary)', padding: 12, borderRadius: 'var(--radius-sm)', marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>HW ID</label>
                  <input
                    type="number"
                    value={newRow.beacon_hw_id}
                    onChange={(e) => setNewRow({ ...newRow, beacon_hw_id: e.target.value })}
                    placeholder="비콘 HW ID"
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 14 }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>유형</label>
                  <select
                    value={newRow.type}
                    onChange={(e) => setNewRow({ ...newRow, type: e.target.value as 'entry' | 'exit' })}
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 14 }}
                  >
                    <option value="entry">입차</option>
                    <option value="exit">출차</option>
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>이름 (선택)</label>
                <input
                  value={newRow.name}
                  onChange={(e) => setNewRow({ ...newRow, name: e.target.value })}
                  placeholder="비콘 이름"
                  style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 14 }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn--sm btn--secondary" onClick={() => setNewRow(null)}>취소</button>
                <button className="btn btn--sm btn--primary" onClick={handleAdd} disabled={saving}>
                  {saving ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          ) : (
            <button
              className="btn btn--secondary"
              onClick={() => setNewRow({ ...emptyRow })}
              style={{ width: '100%' }}
            >
              비콘 추가
            </button>
          )}
        </div>
      )}
    </Modal>
  );
};

export default BeaconManageModal;
